<template>
  <section class="py-16 lg:py-24 bg-neutral-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <!-- Section Header -->
      <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <p class="text-primary-500 text-sm font-semibold uppercase tracking-widest mb-2">Latest News</p>
          <h2 class="text-3xl md:text-4xl font-bold text-neutral-800">
            Blog &amp; Articles
          </h2>
        </div>
        <router-link
          to="/blogs"
          class="inline-flex items-center gap-2 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors group"
        >
          View All Posts
          <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </router-link>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
        <div v-for="n in 3" :key="n" class="bg-white rounded-2xl border border-neutral-100 shadow-sm overflow-hidden animate-pulse">
          <div class="h-52 bg-neutral-200"></div>
          <div class="p-5 space-y-3">
            <div class="h-3 bg-neutral-100 rounded w-1/3"></div>
            <div class="h-4 bg-neutral-200 rounded w-full"></div>
            <div class="h-3 bg-neutral-100 rounded w-5/6"></div>
            <div class="h-3 bg-neutral-100 rounded w-2/3"></div>
          </div>
        </div>
      </div>

      <!-- Blog Grid -->
      <div v-else-if="blogPosts.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
        <article
          v-for="post in blogPosts"
          :key="post.id"
          class="group bg-white rounded-2xl border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
        >
          <!-- Image with zoom -->
          <div class="relative overflow-hidden h-52">
            <img
              v-if="post.image"
              :src="post.image"
              :alt="post.title"
              class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div v-else class="w-full h-full bg-neutral-100 flex items-center justify-center">
              <svg class="w-12 h-12 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <!-- Overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

            <!-- Category pill -->
            <span class="absolute top-3 left-3 bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
              {{ post.category }}
            </span>
          </div>

          <!-- Card Body -->
          <div class="p-5">

            <!-- Date + read time -->
            <div class="flex items-center gap-3 text-xs text-neutral-400 mb-3">
              <div class="flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {{ post.date }}
              </div>
              <span class="w-1 h-1 rounded-full bg-neutral-300"></span>
              <div class="flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ post.readTime }} min read
              </div>
            </div>

            <!-- Title -->
            <h3 class="text-base font-bold text-neutral-800 mb-2 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
              {{ post.title }}
            </h3>

            <!-- Excerpt -->
            <p class="text-sm text-neutral-500 mb-4 leading-relaxed line-clamp-2">
              {{ post.excerpt }}
            </p>

            <!-- Divider -->
            <div class="border-t border-neutral-100 pt-4">
              <!-- Author + Read More -->
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span class="text-xs font-bold text-primary-600">{{ post.authorInitials }}</span>
                  </div>
                  <span class="text-xs font-medium text-neutral-600">{{ post.author }}</span>
                </div>
                <router-link
                  :to="`/blog/${post.id}`"
                  class="inline-flex items-center gap-1 text-xs font-semibold text-primary-500 hover:text-primary-600 transition-colors group/link"
                >
                  Read More
                  <svg class="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </router-link>
              </div>
            </div>

          </div>
        </article>
      </div>

      <!-- Empty state -->
      <div v-else class="text-center py-16 text-neutral-400 text-sm">
        No blog posts available at the moment.
      </div>

    </div>
  </section>
</template>

<script lang="ts">
/* eslint-disable camelcase */
import { Options, Vue } from 'vue-class-component'
import { blogsApi, BlogPost } from '@/api/blogs'

interface UiBlogPost {
  id: number
  title: string
  excerpt: string
  date: string
  category: string
  readTime: number
  author: string
  authorInitials: string
  image: string
}

@Options({})
export default class BlogSection extends Vue {
  loading = true
  blogPosts: UiBlogPost[] = []

  async created () {
    try {
      const res = await blogsApi.list({ limit: 3 })
      this.blogPosts = res.data.data.map((p: BlogPost) => ({
        id: p.id,
        title: p.title,
        excerpt: p.excerpt,
        date: p.published_at
          ? new Date(p.published_at).toLocaleDateString('en-RW', { day: 'numeric', month: 'short', year: 'numeric' })
          : new Date(p.created_at).toLocaleDateString('en-RW', { day: 'numeric', month: 'short', year: 'numeric' }),
        category: p.category,
        readTime: p.read_time,
        author: p.author,
        authorInitials: p.author.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase(),
        image: p.cover_image || ''
      }))
    } catch { } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
